import { getMessageDate, parseTimestampToDate } from './datetime.js';
import { isClientMessage } from './chatMessage.js';
import { isConversationEvaluationActive } from './evaluationTimer.js';
import {
  getDeliveryStatusRank,
  isDeletedStatus,
  isEditedStatus,
  pickMonotonicDeliveryStatus,
} from './messageStatus.js';
import {
  getConversationContact,
  getConversationContactId,
} from './conversationContact.js';

export const normalizeOptionalNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const getConversationCompanyId = (slot) => {
  if (!slot) return null;
  return normalizeOptionalNumber(slot.company_id ?? slot.companyId ?? null);
};

export const getMessageActivityTimestamp = (message) => {
  if (!message) return 0;
  const date = getMessageDate(message);
  return date ? date.getTime() : 0;
};

export const normalizeConversationId = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  if (/^-?\d+$/.test(normalized)) {
    const parsed = Number(normalized);
    if (Number.isSafeInteger(parsed)) return String(parsed);
  }
  return normalized;
};

export const getConversationActivityTimestamp = (slot) => {
  if (!slot) return 0;

  let latest = 0;
  const messages = Array.isArray(slot?.messages) ? slot.messages : [];
  if (messages.length) {
    const lastMessage = messages[messages.length - 1];
    latest = Math.max(latest, getMessageActivityTimestamp(lastMessage));
  }

  const candidates = [
    slot.last_message_at,
    slot.closed_at,
    slot.created_at,
  ];

  candidates.forEach((candidate) => {
    const date = parseTimestampToDate(candidate);
    if (date) {
      latest = Math.max(latest, date.getTime());
    }
  });

  return latest;
};

export const normalizeConversationSortMode = (value) => (
  value === 'arrival' ? 'arrival' : 'lastMessage'
);

export const getConversationArrivalTimestamp = (slot, section = 'attending') => {
  if (!slot) return 0;
  const candidates = section === 'waiting'
    ? [slot.waiting_since, slot.waitingSince, slot.created_at]
    : [slot.attendance_started_at, slot.attendanceStartedAt, slot.created_at];
  for (const candidate of candidates) {
    const date = parseTimestampToDate(candidate);
    if (date) return date.getTime();
  }
  return 0;
};

export const sortConversationList = (list, sortMode = 'lastMessage', section = 'attending') => {
  const items = Array.isArray(list) ? list.slice() : [];
  const normalizedSortMode = normalizeConversationSortMode(sortMode);
  if (normalizedSortMode === 'arrival') {
    return items.sort((a, b) => {
      const diff = getConversationArrivalTimestamp(a, section) - getConversationArrivalTimestamp(b, section);
      if (diff !== 0) return diff;
      return getConversationActivityTimestamp(b) - getConversationActivityTimestamp(a);
    });
  }
  return items.sort((a, b) => {
    const diff = getConversationActivityTimestamp(b) - getConversationActivityTimestamp(a);
    if (diff !== 0) return diff;
    return getConversationArrivalTimestamp(a, section) - getConversationArrivalTimestamp(b, section);
  });
};

export const resolveConversationUnreadCount = (conversation, options = {}) => {
  if (!conversation || typeof conversation !== 'object') return 0;

  const conversationId = normalizeConversationId(
    conversation.id ??
      conversation.conversation_id ??
      conversation.conversationId ??
      conversation.slot_id ??
      conversation.slotId
  );
  const lastOpenMap = options.lastOpenMap && typeof options.lastOpenMap === 'object'
    ? options.lastOpenMap
    : {};
  const incomingUnreadMap =
    options.incomingUnreadMap && typeof options.incomingUnreadMap === 'object'
      ? options.incomingUnreadMap
      : {};
  const isHiddenStatus =
    typeof options.isHiddenStatus === 'function' ? options.isHiddenStatus : () => false;

  const lastOpenTs = Math.max(
    0,
    Number(lastOpenMap?.[conversationId] || lastOpenMap?.[conversation.id] || 0) || 0
  );
  const rawServerUnread = conversation.unread_count ?? conversation.unreadCount;
  const parsedServerUnread = Number(rawServerUnread);
  let serverUnread =
    Number.isFinite(parsedServerUnread) && parsedServerUnread > 0
      ? Math.floor(parsedServerUnread)
      : 0;

  const messages = Array.isArray(conversation.messages) ? conversation.messages : [];
  let messageUnread = 0;
  let latestClientMessageTs = 0;

  messages.forEach((message) => {
    if (!message || typeof message !== 'object') return;
    if (isHiddenStatus(message?.handling_status ?? message?.status)) return;
    if (!isClientMessage(message)) return;

    const ts = getMessageActivityTimestamp(message);
    if (Number.isFinite(ts) && ts > 0) {
      latestClientMessageTs = Math.max(latestClientMessageTs, ts);
      if (ts > lastOpenTs) {
        messageUnread += 1;
      }
    }
  });

  const activityTs = Math.max(
    latestClientMessageTs,
    getConversationActivityTimestamp(conversation)
  );
  if (lastOpenTs > 0 && (!activityTs || activityTs <= lastOpenTs)) {
    serverUnread = 0;
  }

  const localUnreadEntry = conversationId ? incomingUnreadMap?.[conversationId] : null;
  const localOccurredAt = Number(localUnreadEntry?.lastOccurredAt || 0) || 0;
  const localUnread =
    localOccurredAt > lastOpenTs
      ? Math.max(0, Math.floor(Number(localUnreadEntry?.count || 0) || 0))
      : 0;

  return Math.max(0, serverUnread, messageUnread, localUnread);
};

const firstValidTimestampValue = (...values) => {
  for (const value of values) {
    if (parseTimestampToDate(value)) return value;
  }
  return null;
};

export const isConversationOpen = (slot) => {
  const status = String(slot?.status || '').toLowerCase();
  const handling = String(slot?.handling_status || '').toLowerCase();

  // Status finais prevalecem sobre handling_status eventualmente desatualizado
  if (status === 'closed' || status === 'canceled') {
    return false;
  }

  // Se o handling indicar fila/atendimento, considere "aberto" mesmo que o status esteja desalinhado
  if (
    handling.includes('waiting_attendant') ||
    handling.includes('transfer_pending') ||
    handling.includes('waiting_for_team') ||
    handling.includes('attendant_handling') ||
    handling.includes('bot')
  ) {
    return true;
  }

  return status !== 'closed' && status !== 'canceled';
};

export const isConversationAwaitingEvaluation = (slot) => {
  if (isConversationEvaluationActive(slot)) {
    return true;
  }

  const workflowState =
    slot?.workflow_state && typeof slot.workflow_state === 'object'
      ? slot.workflow_state
      : slot?.workflowState && typeof slot.workflowState === 'object'
      ? slot.workflowState
      : null;
  const evaluationStatus = String(
    workflowState?.evaluation_status ??
      workflowState?.evaluationStatus ??
      slot?.evaluation_status ??
      slot?.evaluationStatus ??
      ''
  ).toLowerCase();
  if (['answered', 'completed', 'complete', 'expired', 'canceled', 'cancelled'].includes(evaluationStatus)) {
    return false;
  }

  const handlingStatus = String(slot?.handling_status ?? '').toLowerCase();
  if (handlingStatus.includes('waiting_evaluation')) {
    return true;
  }

  const slotStatus = String(slot?.status ?? '').toLowerCase();
  if (slotStatus.includes('waiting_evaluation')) {
    return true;
  }

  return false;
};

export const isConversationBotHandlingStatus = (slot) => {
  const handlingStatus = String(slot?.handling_status ?? '').toLowerCase();
  const status = String(slot?.status ?? '').toLowerCase();
  return handlingStatus.includes('bot') || status.includes('bot');
};

export const isConversationOperationalWorkflowStatus = (slot) => {
  const handlingStatus = String(slot?.handling_status ?? '').toLowerCase();
  const status = String(slot?.status ?? '').toLowerCase();
  return (
    handlingStatus.includes('workflow_waiting_input') ||
    handlingStatus.includes('workflow_handling') ||
    status.includes('workflow_waiting_input') ||
    status.includes('workflow_handling')
  );
};

const safeParseMessagePayload = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const isConversationClosedByBot = (slot) => {
  const status = String(slot?.status ?? '').toLowerCase();
  if (status !== 'closed') return false;
  const messages = Array.isArray(slot?.messages) ? slot.messages : [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (!msg) continue;
    const msgStatus = String(msg.status ?? '').toLowerCase();
    if (msgStatus !== 'system_log') continue;
    const payload = safeParseMessagePayload(
      msg.message_payload ?? msg.messagePayload ?? null
    );
    const action = String(payload?.action ?? '').toLowerCase();
    if (action === 'closed_by_inactivity' || action === 'closed_by_bot') {
      return true;
    }
    const body = String(msg.message ?? '').toLowerCase();
    if (body.includes('finalizado automaticamente') || body.includes('inatividade')) {
      return true;
    }
  }
  return false;
};

export const isConversationWaitingForTeamStatus = (slot) => {
  const handlingStatus = String(slot?.handling_status ?? '').toLowerCase();
  const status = String(slot?.status ?? '').toLowerCase();
  return handlingStatus.includes('waiting_for_team') || status.includes('waiting_for_team');
};

export const hasConversationSelectedTeam = (slot) => {
  if (!slot || typeof slot !== 'object') return false;
  const teamId =
    slot.team_id ??
    slot.teamId ??
    (typeof slot.team === 'object' && slot.team ? slot.team.id : null);
  return teamId !== null && teamId !== undefined;
};

export const isConversationPendingTeamChoice = (slot) => isConversationWaitingForTeamStatus(slot) && !hasConversationSelectedTeam(slot);

export const isConversationAttendantHandlingStatus = (slot) => {
  const handlingStatus = String(slot?.handling_status ?? '').toLowerCase();
  const status = String(slot?.status ?? '').toLowerCase();
  return handlingStatus.includes('attendant_handling') || status.includes('attendant_handling');
};

export const isConversationAttending = (slot) => Boolean(slot?.in_attendance) || isConversationAttendantHandlingStatus(slot);

const getConversationSessionKey = (slot) => {
  if (!slot || typeof slot !== 'object') return 'primary';
  const raw =
    slot.session_id ??
    slot.sessionId ??
    slot.session ??
    null;
  const normalized = raw === null || raw === undefined ? '' : String(raw).trim();
  return normalized || 'primary';
};

export const getConversationGroupKey = (slot) => {
  if (!slot) return 'slot:unknown';
  const sessionKey = getConversationSessionKey(slot);

  // Prioriza sempre o id do slot para evitar que conversas diferentes (mesmo cliente/telefone) sejam agrupadas
  if (slot.id !== null && slot.id !== undefined) {
    return `slot:${slot.id}`;
  }

  const contactId = getConversationContactId(slot);
  if (contactId !== null && contactId !== undefined) {
    return `contact:${contactId}:session:${sessionKey}`;
  }

  const rawWhatsapp =
    slot.rawWhatsappNumber ||
    slot.whatsappNumber ||
    slot.whatsapp_number ||
    '';
  const digits = rawWhatsapp.toString().replace(/\D/g, '');
  if (digits) {
    return `phone:${digits}:session:${sessionKey}`;
  }

  return 'slot:unknown';
};

export const mergeConversationMessageHistoryMetadata = (currentMessage, incomingMessage) => {
  if (!currentMessage || typeof currentMessage !== 'object') return incomingMessage;
  if (!incomingMessage || typeof incomingMessage !== 'object') return currentMessage;

  const merged = {
    ...currentMessage,
    ...incomingMessage,
    history_conversation_id:
      incomingMessage.history_conversation_id ?? currentMessage.history_conversation_id,
    history_conversation_started_at:
      incomingMessage.history_conversation_started_at ??
      currentMessage.history_conversation_started_at,
    history_is_current_conversation:
      incomingMessage.history_is_current_conversation ??
      currentMessage.history_is_current_conversation,
    history_is_conversation_start:
      incomingMessage.history_is_conversation_start ??
      currentMessage.history_is_conversation_start,
  };

  const currentStatus = currentMessage.status;
  const incomingStatus = incomingMessage.status;
  if (currentStatus || incomingStatus) {
    const currentDeleted = isDeletedStatus(currentStatus);
    const incomingDeleted = isDeletedStatus(incomingStatus);
    if (currentDeleted !== incomingDeleted) {
      merged.status = incomingDeleted ? incomingStatus : currentStatus;
    } else {
      const currentEdited = isEditedStatus(currentStatus);
      const incomingEdited = isEditedStatus(incomingStatus);
      const currentRank = getDeliveryStatusRank(currentStatus);
      const incomingRank = getDeliveryStatusRank(incomingStatus);
      if (currentEdited !== incomingEdited && currentRank === incomingRank) {
        merged.status = incomingEdited ? incomingStatus : currentStatus;
      } else {
        merged.status = pickMonotonicDeliveryStatus(currentStatus, incomingStatus);
      }
    }
  }

  return merged;
};

export const mergeConversationMessages = (slots) => {
  const dedupMap = new Map();
  let fallbackCounter = 0;

  slots.forEach((slot) => {
    const messages = Array.isArray(slot?.messages) ? slot.messages : [];
    messages.forEach((message) => {
      if (!message) return;
      const baileysId =
        message.baileys_message_id ||
        message.baileysMessageId ||
        message.key?.id ||
        null;
      const mediaUrl =
        message.media_url ||
        message.mediaUrl ||
        message.mediaURI ||
        message.mediaUri ||
        message.media_path ||
        message.mediaPath ||
        null;
      const typeHint = String(message.type || '').toLowerCase();
      const isAudioType = typeHint.includes('audio') || typeHint.includes('ptt');
      const key =
        baileysId
          ? `baileys:${baileysId}`
          : message.id !== undefined && message.id !== null
          ? `id:${message.id}`
          : message.message_id !== undefined && message.message_id !== null
          ? `mid:${message.message_id}`
          : isAudioType && mediaUrl
          ? `media:${mediaUrl}`
          : message.timestamp !== undefined && message.timestamp !== null
          ? `ts:${message.timestamp}:${message.sender || ''}`
          : `fallback:${fallbackCounter++}`;
      // Sempre preserva edições/exclusões novas sem apagar metadados de histórico.
      dedupMap.set(key, mergeConversationMessageHistoryMetadata(dedupMap.get(key), message));
    });
  });

  const merged = Array.from(dedupMap.values());

  merged.sort(
    (a, b) => getMessageActivityTimestamp(a) - getMessageActivityTimestamp(b)
  );

  return merged;
};

export const mergeConversationsByClient = (slots) => {
  if (!Array.isArray(slots) || slots.length === 0) {
    return [];
  }

  const grouped = new Map();

  slots.forEach((slot) => {
    const key = getConversationGroupKey(slot);
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(slot);
  });

  const merged = [];

  grouped.forEach((groupSlots, groupKey) => {
    if (groupSlots.length === 1) {
      const [single] = groupSlots;
      const isOpenSingle = isConversationOpen(single);
      const resolvedInAttendance = isConversationAttending(single);
      const hasWaitingSingle =
        isOpenSingle &&
        !resolvedInAttendance &&
        !isConversationAwaitingEvaluation(single);
      const hasAttendingSingle = isOpenSingle && resolvedInAttendance;
      const attendantIdSingleRaw =
        single.attendantId ?? single.attendant_id ?? null;
      const attendantIdsSingle =
        hasAttendingSingle &&
        attendantIdSingleRaw !== null &&
        attendantIdSingleRaw !== undefined
          ? (() => {
              const parsed = Number(attendantIdSingleRaw);
              return Number.isNaN(parsed) ? [] : [parsed];
            })()
          : [];
      merged.push({
        ...single,
        mergedSlotsKey: groupKey,
        mergedSlotIds: [single.id],
        mergedSlotsCount: 1,
        mergedSlotsHasWaiting: hasWaitingSingle,
        mergedSlotsHasAttending: hasAttendingSingle,
        mergedSlotsAttendantIds: attendantIdsSingle,
      });
      return;
    }

    const sortedByRecency = groupSlots
      .slice()
      .sort((a, b) => getConversationActivityTimestamp(b) - getConversationActivityTimestamp(a));

    const openSlots = sortedByRecency.filter((slot) => isConversationOpen(slot));
    const openWaiting = openSlots.filter(
      (slot) =>
        !isConversationAttending(slot) &&
        !isConversationAwaitingEvaluation(slot)
    );
    const openInAttendance = openSlots.filter((slot) => isConversationAttending(slot));

    const candidates =
      openWaiting.length > 0
        ? openWaiting
        : openInAttendance.length > 0
        ? openInAttendance
        : openSlots.length > 0
        ? openSlots
        : sortedByRecency;

    const primary =
      candidates.reduce((best, slot) => {
        if (!best) return slot;
        const slotTs = getConversationActivityTimestamp(slot);
        const bestTs = getConversationActivityTimestamp(best);
        if (slotTs > bestTs) return slot;
        return best;
      }, null) ?? sortedByRecency[0];

    const aggregated = {
      ...primary,
      mergedSlotsKey: groupKey,
      mergedSlotIds: sortedByRecency.map((slot) => slot.id),
      mergedSlotsCount: sortedByRecency.length,
    };

    const mergedMessages = mergeConversationMessages(sortedByRecency);
    aggregated.messages = mergedMessages;

    if (mergedMessages.length > 0) {
      const lastMessage = mergedMessages[mergedMessages.length - 1];
      aggregated.lastMessageSender = lastMessage?.sender || aggregated.lastMessageSender;
      aggregated.last_message_at = firstValidTimestampValue(
        lastMessage?.timestamp,
        lastMessage?.created_at,
        lastMessage?.updated_at,
        aggregated.last_message_at
      );
    }

    const latestTs = sortedByRecency.reduce(
      (acc, slot) => Math.max(acc, getConversationActivityTimestamp(slot)),
      0
    );
    if (latestTs) {
      aggregated.updated_at = new Date(latestTs).toISOString();
    }

    const hasWaiting = openWaiting.length > 0;
    const hasAttending = openInAttendance.length > 0;
    const isBotHandling = isConversationBotHandlingStatus(primary);

    const attendingIds = Array.from(
      new Set(
        openInAttendance
          .map((slot) => {
            const rawId = slot.attendantId ?? slot.attendant_id ?? null;
            if (rawId === null || rawId === undefined) return null;
            const parsed = Number(rawId);
            if (Number.isNaN(parsed)) return null;
            return parsed;
          })
          .filter((value) => value !== null)
      )
    );

    aggregated.in_attendance = !hasWaiting && hasAttending && !isBotHandling;
    aggregated.isReserved = openWaiting.some((slot) => Boolean(slot.isReserved));
    aggregated.status = primary.status;
    aggregated.handling_status = primary.handling_status;
    aggregated.attendantId = primary.attendantId;
    aggregated.attendantName = primary.attendantName;
    aggregated.initiatedBy = primary.initiatedBy ?? aggregated.initiatedBy;
    aggregated.queueSource = primary.queueSource ?? aggregated.queueSource;
    aggregated.isDeviceQueue = primary.isDeviceQueue ?? aggregated.isDeviceQueue;
    aggregated.isOutboundInitiated = sortedByRecency.some(
      (slot) => Boolean(slot.isOutboundInitiated)
    );

    const highestUnread = sortedByRecency.reduce((max, slot) => {
      const unread =
        Number(slot?.unread_count ?? slot?.unreadCount ?? 0) || 0;
      return unread > max ? unread : max;
    }, 0);
    aggregated.unread_count = highestUnread;
    aggregated.unreadCount = highestUnread;

    // Evita marcar conversas em bot como "atendendo" para a fila humana
    aggregated.mergedSlotsHasWaiting = hasWaiting;
    aggregated.mergedSlotsHasAttending = isBotHandling ? false : hasAttending;
    aggregated.mergedSlotsAttendantIds = attendingIds;

    merged.push(aggregated);
  });

  merged.sort(
    (a, b) => getConversationActivityTimestamp(b) - getConversationActivityTimestamp(a)
  );

  return merged;
};

export const mergeAggregatedWithConversation = (aggregated, slot) => {
  if (!aggregated) return slot;

  const mergedSlotIds = Array.from(new Set([...(aggregated.mergedSlotIds || []), slot.id]));

  const mergedMessages = mergeConversationMessages([aggregated, slot]);

  const lastMessage = mergedMessages.length > 0 ? mergedMessages[mergedMessages.length - 1] : null;
  const latestActivity = Math.max(
    getConversationActivityTimestamp(aggregated),
    getConversationActivityTimestamp(slot)
  );

  const updated = {
    ...aggregated,
    mergedSlotsCount: mergedSlotIds.length,
    mergedSlotIds,
    mergedSlotsKey: aggregated.mergedSlotsKey || getConversationGroupKey(slot),
    messages: mergedMessages,
    lifecycle_events:
      Array.isArray(slot.lifecycle_events) && slot.lifecycle_events.length > 0
        ? slot.lifecycle_events
        : aggregated.lifecycle_events ?? [],
    workflow_state:
      slot.workflow_state !== undefined
        ? slot.workflow_state
        : slot.workflowState !== undefined
          ? slot.workflowState
          : aggregated.workflow_state ?? aggregated.workflowState ?? null,
    workflowState:
      slot.workflow_state !== undefined
        ? slot.workflow_state
        : slot.workflowState !== undefined
          ? slot.workflowState
          : aggregated.workflow_state ?? aggregated.workflowState ?? null,
    protocol: slot.protocol ?? aggregated.protocol ?? null,
    lastMessageSender: lastMessage?.sender || aggregated.lastMessageSender,
    last_message_at: firstValidTimestampValue(
      lastMessage?.timestamp,
      lastMessage?.created_at,
      lastMessage?.updated_at,
      slot.last_message_at,
      aggregated.last_message_at
    ),
    waiting_since:
      slot.waiting_since ??
      slot.waitingSince ??
      aggregated.waiting_since ??
      aggregated.waitingSince ??
      null,
    waitingSince:
      slot.waitingSince ??
      slot.waiting_since ??
      aggregated.waitingSince ??
      aggregated.waiting_since ??
      null,
    attendance_started_at:
      slot.attendance_started_at ??
      slot.attendanceStartedAt ??
      aggregated.attendance_started_at ??
      aggregated.attendanceStartedAt ??
      null,
    attendanceStartedAt:
      slot.attendanceStartedAt ??
      slot.attendance_started_at ??
      aggregated.attendanceStartedAt ??
      aggregated.attendance_started_at ??
      null,
    updated_at: latestActivity
      ? new Date(latestActivity).toISOString()
      : aggregated.updated_at ?? slot.updated_at,
    status: slot.status || aggregated.status,
    handling_status: slot.handling_status || aggregated.handling_status,
    attendantId: slot.attendantId ?? aggregated.attendantId,
    attendantName: slot.attendantName || aggregated.attendantName,
    transfer_request_status:
      slot.transfer_request_status ?? aggregated.transfer_request_status ?? null,
    transfer_request_summary:
      slot.transfer_request_summary ?? aggregated.transfer_request_summary ?? null,
    transfer_request_from_attendant_id:
      slot.transfer_request_from_attendant_id ??
      aggregated.transfer_request_from_attendant_id ??
      null,
    transfer_request_from_attendant_name:
      slot.transfer_request_from_attendant_name ??
      aggregated.transfer_request_from_attendant_name ??
      null,
    transfer_request_to_attendant_id:
      slot.transfer_request_to_attendant_id ??
      aggregated.transfer_request_to_attendant_id ??
      null,
    transfer_request_to_attendant_name:
      slot.transfer_request_to_attendant_name ??
      aggregated.transfer_request_to_attendant_name ??
      null,
    transfer_request_created_at:
      slot.transfer_request_created_at ??
      aggregated.transfer_request_created_at ??
      null,
    transfer_request_responded_at:
      slot.transfer_request_responded_at ??
      aggregated.transfer_request_responded_at ??
      null,
    queueSource: slot.queueSource || aggregated.queueSource || null,
    isDeviceQueue: slot.isDeviceQueue ?? aggregated.isDeviceQueue ?? false,
  };
  updated.contact =
    getConversationContact(slot) ||
    getConversationContact(aggregated) ||
    null;

  const openSlots = [aggregated, slot].filter((s) => isConversationOpen(s));
  const openWaiting = openSlots.filter(
    (s) =>
      !isConversationAttending(s) &&
      !isConversationAwaitingEvaluation(s)
  );
  const openInAttendance = openSlots.filter((s) => isConversationAttending(s));
  const isBotHandling = isConversationBotHandlingStatus(slot) || isConversationBotHandlingStatus(aggregated);

  updated.mergedSlotsHasWaiting = openWaiting.length > 0;
  updated.mergedSlotsHasAttending = !isBotHandling && openInAttendance.length > 0;

  const attendingIds = Array.from(
    new Set(
      [...(aggregated.mergedSlotsAttendantIds || []), ...(slot.mergedSlotsAttendantIds || []), ...(slot.attendantId ? [slot.attendantId] : [])]
        .map((v) => (v === null || v === undefined ? null : Number(v)))
        .filter((v) => v !== null && !Number.isNaN(v))
    )
  );

  updated.mergedSlotsAttendantIds = attendingIds;

  const highestUnread = Math.max(Number(aggregated.unread_count || aggregated.unreadCount || 0) || 0, Number(slot.unread_count || slot.unreadCount || 0) || 0);
  updated.unread_count = highestUnread;
  updated.unreadCount = highestUnread;
  updated.in_attendance = isBotHandling ? false : (isConversationAttending(slot) || isConversationAttending(aggregated));

  return updated;
};

export const computeConversationListsFromMerged = (
  mergedSlots,
  attendantId,
  companyId,
  isAdmin = false,
  waitingSlotIds = null,
  sortMode = 'lastMessage'
) => {
  const normalizedAttendantId =
    attendantId === null || attendantId === undefined ? null : Number(attendantId);
  const normalizedCompanyId = normalizeOptionalNumber(companyId);
  const waitingIdsSet =
    waitingSlotIds === null || waitingSlotIds === undefined
      ? null
      : waitingSlotIds instanceof Set
        ? new Set(
            [...waitingSlotIds]
              .map((id) => (id === null || id === undefined ? null : String(id)))
              .filter(Boolean)
          )
        : new Set(
            (Array.isArray(waitingSlotIds) ? waitingSlotIds : [waitingSlotIds])
              .map((id) => (id === null || id === undefined ? null : String(id)))
              .filter(Boolean)
          );

  const slotMatchesWaitingSet = (slot) => {
    if (waitingIdsSet === null) return null;
    const ids =
      Array.isArray(slot?.mergedSlotIds) && slot.mergedSlotIds.length > 0
        ? slot.mergedSlotIds
        : [slot?.id];
    return ids.some(
      (id) =>
        id !== null &&
        id !== undefined &&
        waitingIdsSet.has(String(id))
    );
  };

  const scopedSlots =
    normalizedCompanyId === null
      ? mergedSlots.filter(Boolean)
      : mergedSlots.filter((slot) => getConversationCompanyId(slot) === normalizedCompanyId);

  const waiting = scopedSlots
    .filter((slot) => {
      if (isConversationBotHandlingStatus(slot)) return false;
      if (!isConversationOpen(slot)) return false;
      if (isConversationAwaitingEvaluation(slot)) return false;
      const isOperationalWorkflow = isConversationOperationalWorkflowStatus(slot);
      if (isOperationalWorkflow) return false;
      if (isConversationWaitingForTeamStatus(slot)) return false;
      if (isConversationAttending(slot)) return false;
      const waitingByRoute = slotMatchesWaitingSet(slot);

      const hasWaitingFlag =
        waitingByRoute !== null
          ? waitingByRoute
          : slot.mergedSlotsHasWaiting !== undefined
            ? Boolean(slot.mergedSlotsHasWaiting)
            : !isConversationAttending(slot);

      if (isAdmin) {
        return hasWaitingFlag;
      }

      if (!hasWaitingFlag) return false;

      if (Number.isNaN(normalizedAttendantId)) return false;

      const slotAttendantId = slot.attendantId ?? slot.attendant_id ?? null;
      const normalizedSlotAttendantId =
        slotAttendantId === null || slotAttendantId === undefined
          ? null
          : Number(slotAttendantId);

      if (
        normalizedSlotAttendantId !== null &&
        normalizedAttendantId !== null &&
        !Number.isNaN(normalizedSlotAttendantId) &&
        !Number.isNaN(normalizedAttendantId) &&
        normalizedSlotAttendantId !== normalizedAttendantId
      ) {
        return false;
      }

      if (slot.isReserved) {
        if (normalizedAttendantId === null) return false;
        if (slotAttendantId === null || slotAttendantId === undefined) return false;
        return Number(slotAttendantId) === normalizedAttendantId;
      }

      if (slotAttendantId === null || slotAttendantId === undefined) return true;

      if (normalizedAttendantId === null) return false;

      return Number(slotAttendantId) === normalizedAttendantId;
    });

  const attending = scopedSlots
    .filter((slot) => {
      const waitingByRoute = slotMatchesWaitingSet(slot);
      if (waitingByRoute === true) return false;
      if (!isConversationOpen(slot)) return false;
      const isBotHandling = isConversationBotHandlingStatus(slot);
      const isOperationalWorkflow = isConversationOperationalWorkflowStatus(slot);
      if (isOperationalWorkflow) return false;
      const baseAttendingFlag =
        slot.mergedSlotsHasAttending !== undefined ? Boolean(slot.mergedSlotsHasAttending) : isConversationAttending(slot);

      if (isAdmin) {
        return (
          baseAttendingFlag ||
          (isBotHandling && baseAttendingFlag)
        );
      }

      if (!baseAttendingFlag) return false;

      if (normalizedAttendantId === null || Number.isNaN(normalizedAttendantId)) return false;

      const attendantCandidates =
        Array.isArray(slot.mergedSlotsAttendantIds) && slot.mergedSlotsAttendantIds.length > 0
          ? slot.mergedSlotsAttendantIds
          : [slot.attendantId];

      const matchesAttendant = attendantCandidates.some((id) => {
        const parsed = Number(id);
        if (Number.isNaN(parsed)) return false;
        return parsed === normalizedAttendantId;
      });

      if (isBotHandling) {
        // Conversas em bot só aparecem aqui se já estiverem reservadas/atribuídas para o atendente atual
        if (slot.isReserved) {
          const rawId = slot.attendantId;
          if (rawId === null || rawId === undefined) return false;
          const parsed = Number(rawId);
          if (Number.isNaN(parsed)) return false;
          return parsed === normalizedAttendantId;
        }
        return matchesAttendant;
      }

      return matchesAttendant;
    });

  const all = scopedSlots
    .filter((slot) => (
      isConversationOpen(slot) &&
      !isConversationAwaitingEvaluation(slot) &&
      !isConversationOperationalWorkflowStatus(slot)
    ));

  return {
    waiting: sortConversationList(waiting, sortMode, 'waiting'),
    attending: sortConversationList(attending, sortMode, 'attending'),
    all: sortConversationList(all, sortMode, 'attending'),
  };
};
